import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../utils/translations';
import { Calendar, CreditCard, Wallet, TrendingUp, Clock, CheckCircle, AlertCircle, Loader2, ArrowUpCircle, Check, Package, Zap, Gem, Rocket, Crown, X, ChevronLeft, ChevronRight, ShieldCheck, Copy, BarChart3, Layers, FileText, Plus, RefreshCw, RotateCcw, Pencil } from 'lucide-react';
import { toast } from 'react-toastify';
import { fetchDatasets, fetchDatasetStatus, syncDataset, fetchManualOverrideSummary, resetManualOverrides } from '../api.js';
import {
  confirmResetManualOverrides,
  manualOverrideActiveLabel,
  manualOverrideStatusChipLabel,
  manualOverrideResetOnlySuccessToast,
} from '../utils/manualOverrideUi.js';
import {
  getPlanInfo,
  resolvePlanTxLimit,
  exceedsTxPlanLimit,
  isUnlimitedPlan,
  formatTxLimitDisplay,
  formatPlanLabel,
} from '../utils/planUtils';
import DatasetInvoicesModal from './DatasetInvoicesModal';
import ExchangeImportsManager from './ExchangeImportsManager';
import ModalMotionShell from './ModalMotionShell';
import ModalDragHandle from './ModalDragHandle';

// 🌍 Länderliste für Flaggen-Icons (konsistent mit App.jsx)
const getAvailableCountries = () => [
  { code: 'US', name: 'United States', currency: 'USD' },
  { code: 'DE', name: 'Germany', currency: 'EUR' },
  { code: 'IT', name: 'Italy', currency: 'EUR' },
  { code: 'GR', name: 'Greece', currency: 'EUR' },
  { code: 'UK', name: 'United Kingdom', currency: 'GBP' },
];

// 🚀 Drag Hook (wie in RoadmapModal & HowV3TaxWorksModal)
function useDraggable() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;

    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;

    const onMove = (ev) => {
      setPos({
        x: ev.clientX - startX,
        y: ev.clientY - startY,
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return { ref, pos, onMouseDown, setPos };
}

// 🔎 Normalisiert Plan-Strings ("Ultimate", "ultimate", "Ultimate Plan" etc.) → stabile IDs
const normalizePlanId = (raw) => {
  const s = (raw ?? "").toString().trim().toLowerCase();
  if (!s) return "free";
  if (s === "dev" || s.includes("dev") || s.includes("admin")) return "dev";
  if (s.includes("ultimate")) return "ultimate";
  if (s.includes("elite")) return "elite";
  if (s.includes("pro")) return "pro";
  if (s.includes("standard")) return "standard";
  if (s.includes("free")) return "free";
  return s;
};

// ✅ Re-usable Plan Badge Icon (Free/Standard/Pro/Elite/Ultimate) – konsistent & "premium"
const PlanBadge = ({ planId, size = "sm", variant = "hero", className = "", title }) => {
  const id = normalizePlanId(planId);

  const styles = {
    // Free = ✅ grüner Haken
    free: {
      Icon: Check,
      bg: "from-emerald-500 via-green-600 to-lime-500",
      ring: "ring-emerald-200/70",
      glow: "from-emerald-200/60 via-lime-200/50 to-green-300/60",
    },

    // Standard = Karton
    standard: {
      Icon: Package,
      bg: "from-slate-500 via-slate-600 to-sky-600",
      ring: "ring-slate-200/60",
      glow: "from-slate-300/40 via-sky-300/40 to-blue-400/40",
    },

    // Pro = Blitz (lila)
    pro: {
      Icon: Zap,
      // VeChain-ish: blau/purple, dunkler, kein Pink
      bg: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
      ring: "ring-indigo-200/60",
      glow: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
    },

    // Elite = Diamant (gelb)
    elite: {
      Icon: Gem,
      bg: "from-amber-400 via-yellow-500 to-orange-500",
      ring: "ring-amber-200/70",
      glow: "from-amber-200/60 via-yellow-200/55 to-orange-200/55",
    },

    // Ultimate = Rakete (rot)
    ultimate: {
      Icon: Rocket,
      bg: "from-red-500 via-rose-600 to-orange-500",
      ring: "ring-rose-200/60",
      glow: "from-red-300/55 via-rose-300/50 to-orange-300/50",
    },

    // Dev
    dev: {
      Icon: Crown,
      bg: "from-blue-600 via-indigo-600 to-violet-700",
      ring: "ring-blue-200/60",
      glow: "from-blue-300/50 via-indigo-300/45 to-violet-400/50",
    },
  };

  const cfg = styles[id] || styles.free;

  const sizes = {
    // 🔥 für Header: kleiner & cleaner
    xxs: { wrap: "w-4 h-4 rounded-md", icon: "w-2.5 h-2.5", sparkle: false },
    xs:  { wrap: "w-5 h-5 rounded-lg", icon: "w-3 h-3", sparkle: false },

    // Standard UI
    sm:  { wrap: "w-8 h-8 rounded-xl", icon: "w-4 h-4", sparkle: true,  sparklePos: "top-1.5 right-1.5", sparkleClass: "w-2 h-2" },
    lg:  { wrap: "w-16 h-16 rounded-2xl", icon: "w-8 h-8", sparkle: true, sparklePos: "top-2 right-2", sparkleClass: "w-2.5 h-2.5" },
  };

  const s = sizes[size] || sizes.sm;
  const Icon = cfg.Icon;

  const compact = variant === "compact";

  return (
    <div
      title={title}
      className={[
        "relative inline-flex items-center justify-center overflow-hidden group",
        s.wrap,
        "bg-gradient-to-br",
        cfg.bg,

        // compact: dezent im Header / hero: fett im Dropdown
        compact ? "border border-white/20 shadow-sm" : `ring-1 ${cfg.ring} shadow-lg`,
        "transform-gpu transition-all duration-300",
        compact ? "" : "hover:shadow-xl hover:scale-[1.03]",
        className,
      ].join(" ")}
    >
      {/* Outer glow (nur hero) */}
      {!compact && (
        <div className={`absolute inset-0 bg-gradient-to-br ${cfg.glow} opacity-40 blur-lg`} />
      )}

      {/* Inner highlight + Depth */}
      <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/35 to-transparent opacity-40" />
      <div className="absolute inset-0 rounded-[inherit] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.25)]" />

      {/* Shine sweep (nur hero) */}
      {!compact && (
        <div className="absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/20 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />
      )}

      {/* Icon */}
      <Icon className={`${s.icon} text-white drop-shadow-sm relative z-10`} />

      {/* Sparkle (nur hero + nur sm/lg) */}
      {!compact && s.sparkle && (
        <div className={`absolute ${s.sparklePos} ${s.sparkleClass} bg-white/70 rounded-full shadow-sm`} />
      )}
    </div>
  );
};

// 🎨 Helper: plan-farbige Hover-Styles für Chips und Selects
const getPlanAccent = (planIdOrLabel) => {
  const id = normalizePlanId(planIdOrLabel);

  switch (id) {
    case 'pro':
      return {
        border: "hover:border-indigo-300/80",
        ring: "group-hover:ring-2 group-hover:ring-indigo-200/70",
        glow: "from-transparent via-indigo-200/45 to-transparent",
      };
    case 'elite':
      return {
        border: "hover:border-amber-300/80",
        ring: "group-hover:ring-2 group-hover:ring-amber-200/70",
        glow: "from-transparent via-amber-200/45 to-transparent",
      };
    case 'ultimate':
      return {
        border: "hover:border-rose-300/80",
        ring: "group-hover:ring-2 group-hover:ring-rose-200/70",
        glow: "from-transparent via-rose-200/45 to-transparent",
      };
    case 'standard':
      return {
        border: "hover:border-slate-300/80",
        ring: "group-hover:ring-2 group-hover:ring-slate-200/70",
        glow: "from-transparent via-slate-200/40 to-transparent",
      };
    case 'free':
      return {
        border: "hover:border-slate-300/70",
        ring: "group-hover:ring-2 group-hover:ring-slate-200/60",
        glow: "from-transparent via-slate-200/35 to-transparent",
      };
    case 'dev':
      return {
        border: "hover:border-emerald-300/80",
        ring: "group-hover:ring-2 group-hover:ring-emerald-200/70",
        glow: "from-transparent via-emerald-200/45 to-transparent",
      };
    default:
      return {
        border: "hover:border-blue-300/80",
        ring: "group-hover:ring-2 group-hover:ring-blue-200/70",
        glow: "from-transparent via-blue-200/40 to-transparent",
      };
  }
};

const getCountryMeta = (code) => {
  const normalized = (code || 'DE').toString().toUpperCase();
  const country = getAvailableCountries().find((c) => c.code === normalized);
  return {
    code: normalized,
    name: country?.name || normalized,
    flagUrl: `https://flagcdn.com/w20/${normalized.toLowerCase()}.png`,
  };
};

const getDatasetUsageMeta = (dataset, language = 'de') => {
  const effectivePlan = dataset?.plan_effective || dataset?.plan || 'Free';
  const planLimit = resolvePlanTxLimit(effectivePlan, dataset?.plan_limit);
  const isUnlimited = planLimit == null || isUnlimitedPlan(effectivePlan);

  const used =
    dataset?.plan_used != null
      ? Number(dataset.plan_used)
      : Number(dataset?.total_transactions || 0);

  const usagePercent =
    !isUnlimited && planLimit > 0 ? (used / planLimit) * 100 : 0;

  return {
    effectivePlan,
    planLimit,
    isUnlimited,
    limitLabel: isUnlimited
      ? formatTxLimitDisplay(effectivePlan, language === 'en' ? 'en' : 'de')
      : null,
    used,
    usagePercent,
    pct: Math.round(usagePercent),
  };
};

function MyWalletYearTabs({ years, selectedYear, onChange }) {
  if (!Array.isArray(years) || years.length === 0) return null;

  return (
    <div className="max-w-full overflow-x-auto">
      <div className="inline-flex items-center gap-1 rounded-2xl border border-slate-200/80 bg-white/90 p-1 shadow-sm">
        {years.map((year) => {
          const active = String(year) === String(selectedYear);

          return (
            <button
              key={year}
              type="button"
              onClick={() => onChange(String(year))}
              className={[
                "h-9 shrink-0 rounded-xl px-4 text-sm font-semibold tabular-nums transition-all duration-200",
                active
                  ? "bg-blue-600 text-white shadow-[0_8px_18px_rgba(37,99,235,0.22)]"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-800",
              ].join(" ")}
              aria-pressed={active}
            >
              {year}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatWalletSetShortRef(dataset) {
  const id = dataset?.id;
  if (id == null || id === '') return '—';
  return `#${id}`;
}

function MyWalletSetSwitcher({ activeDataset, activeIndex, totalSets, onPrevious, onNext, language }) {
  const hasMultiple = totalSets > 1;
  const current = totalSets > 0 ? activeIndex + 1 : 0;
  return (
    <div className="flex min-w-0 items-center justify-end gap-2">
      <div className="hidden min-w-0 items-center gap-2 xl:flex">
        <span className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
          {language === 'en' ? 'Wallet Set' : 'Wallet Set'}
        </span>
        <span className="max-w-[320px] truncate rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold tabular-nums text-slate-800 shadow-sm">
          {formatWalletSetShortRef(activeDataset)}
        </span>
      </div>

      <div className="flex h-9 items-center gap-1 rounded-2xl border border-slate-200/80 bg-white p-1 shadow-sm">
        <button
          type="button"
          onClick={onPrevious}
          disabled={!hasMultiple || activeIndex <= 0}
          className="flex h-7 w-7 items-center justify-center rounded-xl text-slate-500 transition hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-30"
          aria-label={language === 'en' ? 'Previous set' : 'Vorheriges Set'}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="min-w-[54px] text-center text-sm font-semibold tabular-nums text-slate-700">
          {current} / {Math.max(totalSets, 1)}
        </div>

        <button
          type="button"
          onClick={onNext}
          disabled={!hasMultiple || activeIndex >= totalSets - 1}
          className="flex h-7 w-7 items-center justify-center rounded-xl text-slate-500 transition hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-30"
          aria-label={language === 'en' ? 'Next set' : 'Nächstes Set'}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function MyWalletMetricTile({ icon: Icon, label, value, subValue, tone = 'blue' }) {
  const toneClass = {
    blue: 'bg-blue-50 text-blue-700 ring-blue-100',
    indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    violet: 'bg-violet-50 text-violet-700 ring-violet-100',
    slate: 'bg-slate-50 text-slate-600 ring-slate-100',
    rose: 'bg-rose-50 text-rose-700 ring-rose-100',
    amber: 'bg-amber-50 text-amber-800 ring-amber-100',
  }[tone] || 'bg-blue-50 text-blue-700 ring-blue-100';

  return (
    <div className="flex h-full min-h-0 flex-col rounded-[22px] border border-slate-200/80 bg-white px-4 py-3 shadow-sm">
      <div className="flex min-h-0 flex-1 items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[14px] ring-1 ${toneClass}`}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div className="min-w-0">
          <div className="truncate text-xs font-semibold uppercase tracking-[0.10em] text-slate-400">{label}</div>
          <div className="mt-0.5 truncate text-base font-semibold leading-tight text-slate-900">{value}</div>
          {subValue && (
            <div className="mt-0.5 line-clamp-2 text-sm font-normal leading-snug text-slate-500">{subValue}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MyWalletStatusTile({
  hasSnapshot,
  manualOverrideCount = 0,
  language = 'de',
  isResetting = false,
  onResetManualOverrides,
}) {
  const ready = hasSnapshot;
  const toneClass = ready
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
    : 'bg-slate-50 text-slate-600 ring-slate-100';
  const StatusIcon = ready ? CheckCircle : Clock;
  const value = ready
    ? (language === 'en' ? 'Ready' : 'Bereit')
    : (language === 'en' ? 'Open' : 'Offen');
  const subValue = ready
    ? (language === 'en' ? 'Snapshot available' : 'Snapshot verfügbar')
    : (language === 'en' ? 'Calculation needed' : 'Berechnung nötig');
  const hasManualEdits = manualOverrideCount > 0;

  return (
    <div className="flex h-full min-h-0 flex-col rounded-[22px] border border-slate-200/80 bg-white px-4 py-3 shadow-sm">
      <div className={`flex min-h-0 flex-1 gap-3 ${hasManualEdits ? 'items-start' : 'items-center'}`}>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[14px] ring-1 ${toneClass}`}>
          <StatusIcon className="h-[18px] w-[18px]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-[0.10em] text-slate-400">
            {language === 'en' ? 'Status' : 'Status'}
          </div>
          <div className="mt-0.5 text-base font-semibold leading-tight text-slate-900">{value}</div>
          <div className="mt-0.5 text-sm font-normal leading-snug text-slate-500">{subValue}</div>
        </div>
      </div>

      {hasManualEdits ? (
        <div
          className="mt-3 flex items-center gap-2 rounded-[14px] border border-amber-200/80 bg-amber-50/70 px-2 py-1.5"
          title={manualOverrideActiveLabel(manualOverrideCount, language)}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] bg-white/90 text-amber-800 ring-1 ring-amber-100">
            <Pencil className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="text-xs font-semibold text-amber-950">
              {manualOverrideStatusChipLabel(manualOverrideCount, language)}
            </div>
            <div className="text-[11px] font-normal text-amber-800/75">
              {language === 'en' ? 'Saved in this set' : 'In diesem Set gespeichert'}
            </div>
          </div>
          <button
            type="button"
            onClick={onResetManualOverrides}
            disabled={isResetting}
            className="inline-flex shrink-0 items-center gap-1 rounded-[10px] border border-amber-200/90 bg-white px-2 py-1 text-[11px] font-semibold text-amber-950 shadow-sm transition hover:border-amber-300 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isResetting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RotateCcw className="h-3 w-3" />
            )}
            Reset
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MyWalletAddressBox({ label, address, tone = 'blue', onCopy, language }) {
  const toneClass = {
    blue: 'border-blue-100 bg-blue-50/65',
    emerald: 'border-emerald-100 bg-emerald-50/65',
    violet: 'border-violet-100 bg-violet-50/65',
  }[tone] || 'border-blue-100 bg-blue-50/65';
  const dotClass = {
    blue: 'bg-blue-500',
    emerald: 'bg-emerald-500',
    violet: 'bg-violet-500',
  }[tone] || 'bg-blue-500';
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.10em] text-slate-400">
        <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
        {label}
      </div>
      <div className={`flex min-h-[56px] items-center gap-2 rounded-[18px] border px-4 py-2.5 ${toneClass}`}>
        <span className="min-w-0 flex-1 select-text break-all font-mono text-sm leading-relaxed text-slate-700">
          {address || '-'}
        </span>
        {address && (
          <button
            type="button"
            onClick={() => onCopy(address)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/85 text-slate-500 ring-1 ring-slate-200 transition hover:bg-white hover:text-blue-700"
            title={language === 'en' ? 'Copy address' : 'Adresse kopieren'}
          >
            <Copy className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

const MyWalletsDashboard = ({ isOpen, onClose, onWalletSelect, userWallet, onOpenSupport }) => {
  const { language } = useLanguage();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(false);
  const drag = useDraggable();
  const { setPos: setDragPos } = drag;
  const [showInvoices, setShowInvoices] = useState(false);
  const [invoiceContext, setInvoiceContext] = useState(null);
  // Struktur: { year, plan, purchaseId }
  
  // 🚀 NEU: Wallet Sets statt einzelne Wallets
  const [datasets, setDatasets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [datasetsByYear, setDatasetsByYear] = useState({});
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedSetIndexByYear, setSelectedSetIndexByYear] = useState({});
  const [walletPageByDatasetId, setWalletPageByDatasetId] = useState({});
  // 🚀 FIX: Auth-Issue State für 401-Handling
  const [authIssue, setAuthIssue] = useState(false);
  const [manualOverrideCount, setManualOverrideCount] = useState(0);
  const [isResettingManualOverrides, setIsResettingManualOverrides] = useState(false);
  
  // 🚀 NEU: Exchange Imports Modal State
  const [showImportsFor, setShowImportsFor] = useState(null);
  const pendingImportsRef = useRef(null); // Ref für Dataset-ID wenn Modal temporär geschlossen wird

  // 🚀 PHASE 3: State für Wallet-Status-Toggle
  const [togglingWalletId, setTogglingWalletId] = useState(null);

  // ⬇️ NEU: Upgrade-State und Hilfsfunktionen
  const PLAN_ORDER = ['Free', 'Standard', 'Pro', 'Elite', 'Ultimate'];
  
  const getNextPlans = (current) => {
    const idx = Math.max(0, PLAN_ORDER.indexOf(current || 'Free'));
    // nur höhere Pläne
    return PLAN_ORDER.slice(idx + 1);
  };

  const [openUpgradeFor, setOpenUpgradeFor] = useState(null); // datasetId oder null
  const [selectedPlanById, setSelectedPlanById] = useState({});
  const [selectedTokenById, setSelectedTokenById] = useState({});

  // 🚀 NEU: Lade Wallet Sets beim Öffnen des Modals - NUR wenn authentifiziert!
  useEffect(() => {
    if (isOpen && !authLoading && isAuthenticated) {
            loadDatasets();
    } else if (isOpen && !authLoading && !isAuthenticated) {
            setError(language === 'en' ? 'Please log in to view your wallets' : 'Bitte loggen Sie sich ein, um Ihre Wallets anzuzeigen');
    }
  }, [isOpen, authLoading, isAuthenticated]);

  // 🎯 NEU: Höre auf localStorage Änderungen (wenn AnalysisApp das Datum setzt)
  useEffect(() => {
    const handleStorageChange = () => {
      if (isOpen) {
                loadDatasets();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isOpen]);

  // 🚀 NEU: Fallback aus localStorage beim Laden mergen
  const mergeLocalLastCalculated = (list) =>
    list.map(d => {
      const ts = localStorage.getItem(`v3tax:lastCalculatedAt:${String(d.id)}`);
      if (ts && !d.last_calculated_at && !d.lastCalculatedAt) {
        return { ...d, last_calculated_at: ts, snapshot_exists: d.snapshot_exists ?? false };
      }
      return d;
    });

  // 🚀 NEU: Lade Wallet Sets von der neuen API
  const loadDatasets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setAuthIssue(false); // 🚀 FIX: Reset auth issue beim Neuladen
    
    try {
      // Lade Datasets für die letzten 5 Jahre
      const currentYear = new Date().getFullYear();
      const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];
      
      const allDatasets = [];
      
      for (const year of years) {
        try {
          const response = await fetchDatasets(year);
          
          // 🚀 FIX: 401-Handling (fehlendes JWT-Cookie)
          if (response?.unauthorized) {
                        setAuthIssue(true);
            setError(language === 'en' ? 'Please log in again to view your wallet sets' : 'Bitte loggen Sie sich erneut ein, um Ihre Wallet Sets anzuzeigen');
            return; // Stoppe das Laden weiterer Jahre
          }
          
          if (response.success && Array.isArray(response.datasets)) {
                        
            // 🚀 DEBUG: Log Dataset #20 Plan-Info
            const dataset20 = response.datasets.find(d => d.id === 20);
            if (dataset20) {
                          }
            
            allDatasets.push(...response.datasets);
          }
        } catch (yearError) {
                    // Fahre mit dem nächsten Jahr fort
        }
      }
      
      // Fallback-Timestamp mergen
      const merged = mergeLocalLastCalculated(allDatasets);
      
      
      // nach Jahr gruppieren
      const grouped = merged.reduce((acc, d) => {
        (acc[d.year] ||= []).push(d);
        return acc;
      }, {});

      setDatasets(merged);
      setDatasetsByYear(grouped);
      
    } catch (error) {
            setError(language === 'en' ? 'Failed to load wallet sets' : 'Fehler beim Laden der Wallet Sets');
    } finally {
      setIsLoading(false);
    }
  }, [mergeLocalLastCalculated, language]);

  // 🚀 NEU: Höre auf CustomEvent für Dataset-Reload (z.B. nach Upgrade)
  useEffect(() => {
    const onDatasetsReload = async (e) => {
            if (isOpen) {
        await loadDatasets();
      }
    };

    const onDatasetsRefresh = async (e) => {
            if (isOpen) {
        await loadDatasets();
      }
    };

    window.addEventListener('datasets:reload', onDatasetsReload);
    window.addEventListener('datasets:refresh', onDatasetsRefresh);
    return () => {
      window.removeEventListener('datasets:reload', onDatasetsReload);
      window.removeEventListener('datasets:refresh', onDatasetsRefresh);
    };
  }, [isOpen, loadDatasets]);

  // 🚀 NEU: Höre auf CustomEvent von AnalysisApp für sofortige UI-Updates
  useEffect(() => {
    const onCalculationDone = async (e) => {
      const { datasetId, ts } = e.detail || {};
      if (!datasetId || !ts) return;

      const idStr = String(datasetId);
      
      // Snapshot-Flag nur aus DB nachladen — nicht optimistisch setzen (Race mit async saveSnapshot)
      setDatasets(prev =>
        prev.map(d => String(d.id) === idStr ? { ...d, last_calculated_at: ts } : d)
      );
      setDatasetsByYear(prev => {
        const next = {};
        for (const year of Object.keys(prev)) {
          next[year] = prev[year].map(d =>
            String(d.id) === idStr ? { ...d, last_calculated_at: ts } : d
          );
        }
        return next;
      });

      try {
        await loadDatasets();
              } catch (error) {
                // Fallback: Versuche Status zu holen
        try {
          const statusResponse = await fetchDatasetStatus(parseInt(idStr));
          if (statusResponse.success) {
                        // Update local state with backend data
            setDatasets(prev =>
              prev.map(d => String(d.id) === idStr ? { 
                ...d, 
                last_calculated_at: statusResponse.dataset.lastCalculatedAt || ts,
                snapshot_exists: !!statusResponse.dataset.snapshotExists,
                snapshotExists: !!statusResponse.dataset.snapshotExists,
              } : d)
            );
          }
        } catch (statusError) {
                  }
      }
    };

    window.addEventListener('analysis:calculationCompleted', onCalculationDone);
    return () => window.removeEventListener('analysis:calculationCompleted', onCalculationDone);
  }, [language, loadDatasets]);

  // 🚫 ALTE FUNKTIONEN - ENTFERNT: saveLastCalculatedDate, getSyncButtonState, updateSyncLimit, loadLastCalculatedDate
  // Nicht mehr benötigt für Wallet Sets (werden vom Backend verwaltet)

  // 🔒 Hilfsfunktion: Prüft, ob das Plan-Limit für ein Dataset überschritten ist
  const isDatasetOverPlanLimit = (dataset) => {
    if (!dataset) return false;
    const effectivePlan = dataset.plan_effective || dataset.plan;
    const used =
      dataset.plan_used != null
        ? dataset.plan_used
        : (dataset.total_transactions || 0);
    return exceedsTxPlanLimit(used, effectivePlan, dataset.plan_limit);
  };

  // 🚀 NEU: Funktion zum Berechnen eines gesamten Wallet Sets
  const navigateToDatasetAnalysis = (dataset, { autorun, resetManualOverrides = false } = {}) => {
    localStorage.setItem("v3tax_preview_mode", "false");
    localStorage.removeItem("previewWallets");

    const year = dataset.year || new Date().getFullYear().toString();
    const datasetCountry = String(
      dataset.residence_country || dataset.residenceCountry || dataset.selectedCountry || dataset.country || ''
    ).trim().toUpperCase() || undefined;

    navigate(`/analyze/${dataset.id}?year=${year}`, {
      state: {
        datasetId: dataset.id,
        fromViewAnalysis: autorun === "view",
        loadFromSnapshot: autorun === "view",
        year,
        autorun,
        resetManualOverrides,
        isPreview: false,
        isPurchased: true,
        selectedCountry: datasetCountry,
      },
    });
  };

  const ensureDatasetCanCalculate = (dataset) => {
    if (!isDatasetOverPlanLimit(dataset)) return true;

    const effectivePlan = dataset.plan_effective || dataset.plan || "Free";
    const planLimit = resolvePlanTxLimit(effectivePlan, dataset.plan_limit);
    const used =
      dataset.plan_used != null
        ? dataset.plan_used
        : (dataset.total_transactions || 0);

    toast.error(
      language === "en"
        ? `Plan limit exceeded (${used}/${planLimit}). Upgrade is required to start a new calculation.`
        : `Plan-Limit überschritten (${used}/${planLimit}). Ein Plan-Upgrade ist erforderlich, um eine neue Berechnung zu starten.`
    );
    return false;
  };

  const handleCalculateDataset = (dataset) => {
    try {
      if (!ensureDatasetCanCalculate(dataset)) return;
      navigateToDatasetAnalysis(dataset, { autorun: "calc" });
    } catch (error) {
      toast.error(
        language === "en"
          ? "Failed to redirect to analysis"
          : "Fehler beim Weiterleiten zur Analyse"
      );
    }
  };

  const handleResetManualOverridesOnly = async (dataset) => {
    try {
      if (!manualOverrideCount || isResettingManualOverrides) return;
      if (!confirmResetManualOverrides(manualOverrideCount, language, { withRecalculate: false })) {
        return;
      }

      const year = dataset.year || activeYear || new Date().getFullYear().toString();
      setIsResettingManualOverrides(true);
      await resetManualOverrides(dataset.id, year);

      setManualOverrideCount(0);
      window.dispatchEvent(
        new CustomEvent("manualOverrides:updated", {
          detail: { datasetId: dataset.id, count: 0 },
        })
      );

      toast.success(manualOverrideResetOnlySuccessToast(language));
    } catch (error) {
      toast.error(
        language === "en"
          ? "Manual edits could not be removed."
          : "Manuelle Edits konnten nicht entfernt werden."
      );
    } finally {
      setIsResettingManualOverrides(false);
    }
  };

  // 🚀 NEU: Funktion zum Anzeigen der Analyse (wenn Snapshot vorhanden)
  const handleViewDatasetAnalysis = (dataset) => {
    try {
      navigateToDatasetAnalysis(dataset, { autorun: "view" });
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to redirect to analysis' : 'Fehler beim Weiterleiten zur Analyse');
    }
  };

  // Sync = Snapshot invalidieren + frische On-Chain-Daten — manuelle Overrides bleiben in der DB
  const handleSyncDataset = async (dataset) => {
    try {
      const result = await syncDataset(dataset.id);
      
      if (result.success) {
        setDatasets(prev =>
          prev.map(d => d.id === dataset.id ? { 
            ...d, 
            snapshot_exists: result.snapshotExists ?? false, 
            last_calculated_at: result.lastCalculatedAt ?? null,
            syncLimitReached: false
          } : d)
        );
        setDatasetsByYear(prev => {
          const next = {};
          for (const year of Object.keys(prev)) {
            next[year] = prev[year].map(d =>
              d.id === dataset.id ? { 
                ...d, 
                snapshot_exists: result.snapshotExists ?? false, 
                last_calculated_at: result.lastCalculatedAt ?? null,
                syncLimitReached: false
              } : d
            );
          }
          return next;
        });
        
        localStorage.removeItem(`v3tax:lastCalculatedAt:${String(dataset.id)}`);

        try {
          const year = dataset.year || activeYear || new Date().getFullYear().toString();
          const summary = await fetchManualOverrideSummary(dataset.id, year);
          setManualOverrideCount(summary.count || 0);
        } catch {
          const preservedCount = Number(result.manualOverrideCount ?? manualOverrideCount ?? 0);
          if (preservedCount > 0) setManualOverrideCount(preservedCount);
        }

        toast.success(
          language === 'en'
            ? `Dataset synced! ${result.syncsRemaining ?? 0} syncs remaining today.`
            : `Dataset synchronisiert! Noch ${result.syncsRemaining ?? 0} Sync(s) heute verfügbar.`
        );
        
                
      } else {
                toast.error(result.error || (language === 'en' ? 'Sync failed' : 'Synchronisation fehlgeschlagen'));
      }
      
    } catch (error) {
            
      // 429 oder explizite Limit-Message vom Backend
      const isRateLimit =
        error.status === 429 ||
        error.message?.toLowerCase().includes('limit') ||
        error.body?.syncsRemaining === 0;

      if (isRateLimit) {
        // Markiere Dataset als Limit erreicht
        setDatasets(prev =>
          prev.map(d => d.id === dataset.id ? { ...d, syncLimitReached: true } : d)
        );
        setDatasetsByYear(prev => {
          const next = {};
          for (const year of Object.keys(prev)) {
            next[year] = prev[year].map(d =>
              d.id === dataset.id ? { ...d, syncLimitReached: true } : d
            );
          }
          return next;
        });
        
        toast.error(
          language === 'en'
            ? 'Sync limit reached for today (3/24h). Please try again tomorrow.'
            : 'Maximale Sync-Anzahl pro Tag erreicht (3/24h). Bitte morgen erneut versuchen.'
        );
      } else {
        toast.error(
          language === 'en'
            ? 'Failed to sync dataset'
            : 'Fehler beim Synchronisieren'
        );
      }
    }
  };

  // 🚀 NEU: Polling für Dataset-Berechnungsfortschritt
  const pollDatasetProgress = async (datasetId) => {
    const idStr = String(datasetId); // ID als String normalisieren
    const maxAttempts = 30; // 5 Minuten bei 10s Intervall
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await fetchDatasetStatus(Number(datasetId)); // API erwartet Number
        
        if (response.success) {
          const { dataset } = response;
          
          // Aktualisiere den lokalen State - immer String vergleichen
          const updatedDatasets = datasets.map(d => 
            String(d.id) === idStr ? { 
              ...d, 
              snapshot_exists: dataset.snapshotExists,
              last_calculated_at: dataset.lastCalculatedAt
            } : d
          );
          setDatasets(updatedDatasets);
          
          // Aktualisiere auch die gruppierten Datasets
          setDatasetsByYear(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(year => {
              updated[year] = updated[year].map(d => 
                String(d.id) === idStr ? { 
                  ...d, 
                  snapshot_exists: dataset.snapshotExists,
                  last_calculated_at: dataset.lastCalculatedAt
                } : d
              );
            });
            return updated;
          });
          
          // Wenn Berechnung abgeschlossen ist
          if (dataset.snapshotExists) {
            // toast.success(language === 'en' ? 'Dataset calculation completed' : 'Dataset-Berechnung abgeschlossen');
            return;
          }
          
          // Wenn noch nicht abgeschlossen und wir noch Versuche haben
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 10000); // 10 Sekunden warten
          } else {
            toast.warning(language === 'en' ? 'Calculation is taking longer than expected' : 'Berechnung dauert länger als erwartet');
          }
        }
      } catch (error) {
                toast.error(language === 'en' ? 'Failed to check calculation progress' : 'Fehler beim Prüfen des Berechnungsfortschritts');
      }
    };
    
    // Starte Polling
    poll();
  };

  // 🚫 ALTE FUNKTIONEN - ENTFERNT: handleSyncWallet, handleViewAnalysis
  // Ersetzt durch: handleSyncDataset, handleViewDatasetAnalysis

  // 🎯 NEU: Funktion zum Kopieren in die Zwischenablage
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('toast.addressCopied', language));
    } catch (error) {
            toast.error(t('toast.copyFailed', language));
    }
  };

  // 🚀 PHASE 3: Handler für Wallet-Status-Toggle
  const handleToggleWalletStatus = async (datasetId, wallet) => {
    try {
      setTogglingWalletId(wallet.wallet_pair_id);
      
      // 🚀 PHASE 5: Prüfe ob Snapshot vorhanden war (für bessere UX-Kommunikation)
      const dataset = datasets.find(d => d.id === datasetId);
      const hadSnapshot = dataset?.snapshot_exists || dataset?.snapshotExists || false;
      
      const res = await fetch(`/api/datasets/${datasetId}/wallets/${wallet.wallet_pair_id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !wallet.is_active })
      });

      if (!res.ok) {
        toast.error(language === 'en' ? 'Failed to change wallet status' : 'Fehler beim Ändern des Wallet-Status');
        return;
      }

      const data = await res.json();

      // UI aktualisieren
      await loadDatasets();

      if (data.wallet.is_active) {
        // Wallet wurde aktiviert
        toast.success(
          language === 'en' 
            ? 'Wallet reactivated. Please recalculate the set.' 
            : 'Wallet wieder aktiviert. Bitte Set neu berechnen.',
          {
            autoClose: 4000,
            position: 'top-center'
          }
        );
      } else {
        // Wallet wurde deaktiviert
        const limitReached = data.syncLimitReached === true;

        let message;
        if (limitReached && hadSnapshot) {
          // 🔒 Neues Verhalten: Snapshot bleibt bestehen, Neuberechnung erst morgen möglich
          message = language === 'en'
            ? 'Wallet deactivated. Recalculation with this wallet configuration is only possible tomorrow (daily sync limit 3/24h reached).'
            : 'Wallet deaktiviert. Neuberechnung mit diesem Wallet-Set erst morgen möglich (Sync-Limit 3/24h erreicht).';
        } else if (hadSnapshot) {
          // Altes Verhalten: Snapshot invalidiert, jetzt neu berechnen
          message = language === 'en'
            ? 'Wallet deactivated. Snapshot invalidated – please recalculate.'
            : 'Wallet deaktiviert. Snapshot ungültig – bitte neu berechnen.';
        } else {
          // Kein Snapshot vorhanden
          message = language === 'en'
            ? 'Wallet deactivated. It will be ignored in future calculations.'
            : 'Wallet deaktiviert. Sie wird in zukünftigen Berechnungen ignoriert.';
        }

        toast.warning(message, {
          autoClose: 4000,
          position: 'top-center'
        });
      }
    } catch (e) {
            toast.error(language === 'en' ? 'Failed to change wallet status' : 'Fehler beim Ändern des Wallet-Status');
    } finally {
      setTogglingWalletId(null);
    }
  };

  // 🚫 ALTE FUNKTION - ENTFERNT: loadPurchasedWallets
  // Ersetzt durch: loadDatasets() (siehe oben)

  // 🚫 ALTE FUNKTIONEN - ENTFERNT: loadAdminWallets, enrichWalletData
  // Nicht mehr benötigt für Wallet Sets

  // 🚫 ALTE FUNKTIONEN - ENTFERNT: determinePlan, getPlanLimits, findVeDelegateWallet
  // Nicht mehr benötigt für Wallet Sets (werden vom Backend bereitgestellt)

  // Sortiere Jahre absteigend (neueste zuerst)
  const sortedYears = Object.keys(datasetsByYear).sort((a, b) => b - a);

  const activeYear = selectedYear || sortedYears[0] || null;
  const setsForActiveYear = activeYear ? (datasetsByYear[activeYear] || []) : [];
  const activeSetIndexRaw = activeYear ? (selectedSetIndexByYear[activeYear] ?? 0) : 0;
  const activeSetIndex = Math.min(
    Math.max(activeSetIndexRaw, 0),
    Math.max(setsForActiveYear.length - 1, 0)
  );
  const activeDataset = setsForActiveYear[activeSetIndex] || null;
  const activeDatasetId = activeDataset?.id ? String(activeDataset.id) : null;
  const walletsPerPage = isMobile ? 1 : 3;
  const activeWallets = activeDataset?.wallets || [];
  const walletPage = activeDatasetId ? (walletPageByDatasetId[activeDatasetId] || 0) : 0;
  const walletPageCount = Math.max(1, Math.ceil(activeWallets.length / walletsPerPage));
  const safeWalletPage = Math.min(Math.max(walletPage, 0), walletPageCount - 1);
  const visibleWallets = activeWallets.slice(
    safeWalletPage * walletsPerPage,
    safeWalletPage * walletsPerPage + walletsPerPage
  );

  useEffect(() => {
    if (!sortedYears.length) {
      setSelectedYear(null);
      return;
    }

    if (!selectedYear || !datasetsByYear[selectedYear]) {
      setSelectedYear(sortedYears[0]);
    }
  }, [sortedYears.join('|'), selectedYear, datasetsByYear]);

  useEffect(() => {
    if (!activeYear || !setsForActiveYear.length) return;

    setSelectedSetIndexByYear((prev) => {
      const current = prev[activeYear] ?? 0;
      const clamped = Math.min(Math.max(current, 0), setsForActiveYear.length - 1);
      if (current === clamped) return prev;
      return { ...prev, [activeYear]: clamped };
    });
  }, [activeYear, setsForActiveYear.length]);

  useEffect(() => {
    if (!activeDatasetId) return;

    setWalletPageByDatasetId((prev) => {
      const current = prev[activeDatasetId] ?? 0;
      const clamped = Math.min(Math.max(current, 0), walletPageCount - 1);
      if (current === clamped) return prev;
      return { ...prev, [activeDatasetId]: clamped };
    });
  }, [activeDatasetId, walletPageCount]);

  useEffect(() => {
    let cancelled = false;

    const loadManualOverrideCount = async () => {
      if (!activeDataset?.id) {
        if (!cancelled) setManualOverrideCount(0);
        return;
      }

      const year =
        activeDataset.year || activeYear || new Date().getFullYear().toString();

      try {
        const summary = await fetchManualOverrideSummary(activeDataset.id, year);
        if (!cancelled) setManualOverrideCount(summary.count || 0);
      } catch {
        if (!cancelled) setManualOverrideCount(0);
      }
    };

    loadManualOverrideCount();
    return () => {
      cancelled = true;
    };
  }, [activeDataset?.id, activeDataset?.year, activeYear]);

  useEffect(() => {
    const onOverridesUpdated = (event) => {
      if (!activeDataset?.id) return;
      if (String(event.detail?.datasetId) !== String(activeDataset.id)) return;
      setManualOverrideCount(Number(event.detail?.count || 0));
    };

    window.addEventListener("manualOverrides:updated", onOverridesUpdated);
    return () => window.removeEventListener("manualOverrides:updated", onOverridesUpdated);
  }, [activeDataset?.id]);

  const goToPreviousSet = () => {
    if (!activeYear || setsForActiveYear.length <= 1) return;
    setSelectedSetIndexByYear((prev) => ({
      ...prev,
      [activeYear]: Math.max((prev[activeYear] ?? 0) - 1, 0),
    }));
  };

  const goToNextSet = () => {
    if (!activeYear || setsForActiveYear.length <= 1) return;
    setSelectedSetIndexByYear((prev) => ({
      ...prev,
      [activeYear]: Math.min((prev[activeYear] ?? 0) + 1, setsForActiveYear.length - 1),
    }));
  };

  const goToPreviousWalletPage = () => {
    if (!activeDatasetId) return;
    setWalletPageByDatasetId((prev) => ({
      ...prev,
      [activeDatasetId]: Math.max((prev[activeDatasetId] ?? 0) - 1, 0),
    }));
  };

  const goToNextWalletPage = () => {
    if (!activeDatasetId) return;
    setWalletPageByDatasetId((prev) => ({
      ...prev,
      [activeDatasetId]: Math.min((prev[activeDatasetId] ?? 0) + 1, walletPageCount - 1),
    }));
  };

  const openInvoicesForDataset = (dataset) => {
    const pid =
      dataset?.purchase_id ||
      dataset?.purchaseId ||
      dataset?.latestPlan?.purchaseId ||
      dataset?.plan?.purchaseId;

    setInvoiceContext({
      year: dataset?.year || new Date().getFullYear().toString(),
      plan: dataset?.plan_effective || dataset?.plan || null,
      purchaseId: pid,
    });
    setShowInvoices(true);
  };

  // Formatiere Datum mit Uhrzeit
  const formatDateWithTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCompactDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 🚫 ALTE FUNKTION - ENTFERNT: formatPurchaseId
  // Nicht mehr benötigt für Wallet Sets (verwenden dataset.label)

  // Formatiere Wallet-Adresse - Vollständige Adresse für My Wallets Übersicht
  const formatWalletAddress = (address) => {
    if (!address) return '-';
    return address; // 🎯 Vollständige Adresse anzeigen
  };


  // 🚫 ALTE FUNKTION - ENTFERNT: getStatusIcon
  // Nicht mehr benötigt für Wallet Sets (verwenden CheckCircle direkt)

  // Reset position when modal opens (via setPos state)
  // ⚠️ WICHTIG: drag wird bei jedem Render neu erstellt, daher setDragPos extrahieren
  useEffect(() => {
    if (isOpen) setDragPos({ x: 0, y: 0 });
  }, [isOpen, setDragPos]);

  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth < 768);
    };

    updateIsMobile();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateIsMobile);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateIsMobile);
      }
    };
  }, []);

  const getModalStyle = () => {
    if (isMobile) {
      return {
        position: 'fixed',
        top: '136px',
        left: '50%',
        transform: 'translate(-50%, 0)',
        maxHeight: 'calc(100vh - 120px)',
        width: '100%',
      };
    }

    // Desktop: wie RoadmapModal & HowV3TaxWorksModal - einfacher translate (smooth!)
    // Wrapper zentriert das Modal, wir verschieben nur relativ
    return {
      transform: `translate(${drag.pos.x}px, ${drag.pos.y}px)`,
    };
  };

  return (
    <>
    <ModalMotionShell
      isOpen={isOpen}
      onClose={onClose}
      zIndexClass="z-[200]"
      backdropClassName="items-start md:items-center px-3 pb-3 pt-[104px] md:p-2 overflow-y-auto md:overflow-hidden"
    >
      <div
        ref={drag.ref}
        className={`modal-container relative flex w-full flex-col overflow-hidden rounded-[30px] border border-slate-200/90 bg-white shadow-[0_32px_105px_rgba(15,23,42,0.21)] ${
          isAuthenticated
            ? 'md:w-[98.5vw] md:max-w-[1880px] md:min-h-[920px] md:h-[min(980px,calc(100vh-20px))] max-h-[calc(100vh-20px)]'
            : 'max-h-[92vh] md:max-w-[920px]'
        }`}
        style={{ ...getModalStyle(), zIndex: 1000 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.preventDefault()}
      >
        <div className="h-[5px] shrink-0 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600" />

        <div
          className="shrink-0 cursor-move select-none border-b border-slate-200 bg-white/95 px-5 py-3.5 md:px-6"
          onMouseDown={(e) => {
            if (isMobile) return;
            e.preventDefault();
            e.stopPropagation();
            drag.onMouseDown(e);
          }}
        >
          <div className="flex h-[56px] items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              {!isMobile && (
                <ModalDragHandle
                  onMouseDown={drag.onMouseDown}
                  title={language === 'en' ? 'Move' : 'Verschieben'}
                  ariaLabel={language === 'en' ? 'Move modal' : 'Modal verschieben'}
                  className="shrink-0"
                />
              )}

              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 text-white shadow-[0_10px_24px_rgba(37,99,235,0.24)] ring-1 ring-white/60">
                <ShieldCheck className="h-[22px] w-[22px]" />
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">
                    V3Tax Wallet Console
                  </span>
                  <span className="hidden rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium uppercase tracking-[0.12em] text-slate-500 md:inline-flex">
                    {language === 'en' ? 'Purchased Sets' : 'Gekaufte Sets'}
                  </span>
                </div>
            <p className="mt-1 truncate text-sm font-normal text-slate-500">
              {language === 'en'
                ? 'Tax years, wallet sets, limits and calculation status in one compact overview'
                    : 'Steuerjahre, Wallet-Sets, Limits und Berechnungsstatus in einer kompakten Übersicht'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              onMouseDown={(e) => e.stopPropagation()}
              className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-400 shadow-sm transition hover:border-slate-300 hover:text-slate-700 hover:shadow-md"
              aria-label={language === 'en' ? 'Close' : 'Schließen'}
            >
              <X className="h-5 w-5 transition-transform duration-200 group-hover:rotate-90" />
            </button>
          </div>
        </div>

        <div
          className={
            isMobile
              ? 'relative flex-1 min-h-0 overflow-y-auto bg-slate-50/70 p-4'
              : 'relative flex-1 min-h-0 overflow-hidden bg-slate-50/70 p-5 md:p-6'
          }
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.08),transparent_32%),radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.06),transparent_34%)]" />
          <div className="relative z-10 h-full min-h-0">
          {isLoading ? (
            <div className="rounded-[28px] border border-slate-200/90 bg-gradient-to-b from-slate-50 via-white to-blue-50/40 px-6 py-16 text-center shadow-inner">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-[26px] bg-white shadow-[0_18px_46px_rgba(37,99,235,0.18)] ring-1 ring-blue-100">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">
                {language === 'en' ? 'Loading wallets…' : 'Wallets werden geladen…'}
              </h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                {language === 'en'
                  ? 'Please wait while we fetch your purchased wallet analyses.'
                  : 'Bitte warten Sie, während wir Ihre gekauften Wallet-Analysen abrufen.'}
              </p>
            </div>
          ) : error ? (
            <div className="rounded-[28px] border border-slate-200 bg-white px-6 py-14 text-center shadow-[0_18px_48px_rgba(15,23,42,0.06)]">
              {error.includes('log in') || error.includes('loggen Sie sich ein') ? (
                <>
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-[26px] bg-blue-50 ring-1 ring-blue-100">
                    <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">{error}</h3>
                  <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
                    {language === 'en'
                      ? 'Please log in to access your dashboard and perform wallet analyses.'
                      : 'Bitte loggen Sie sich ein, um Zugriff auf Ihr Dashboard zu erhalten und Wallet-Analysen durchführen zu können.'}
                  </p>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-[26px] bg-rose-50 ring-1 ring-rose-100">
                    <AlertCircle className="h-10 w-10 text-rose-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">{error}</h3>
                  <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
                    {language === 'en'
                      ? 'Failed to load purchased wallets. Please try again later.'
                      : 'Fehler beim Laden der gekauften Wallets. Bitte versuchen Sie es später erneut.'}
                  </p>
                  <button
                    type="button"
                    onClick={loadDatasets}
                    className="mt-8 rounded-2xl bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700"
                  >
                    {language === 'en' ? 'Retry' : 'Erneut versuchen'}
                  </button>
                </>
              )}
            </div>
          ) : datasets.length === 0 ? (
            <div className="rounded-[28px] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-14 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-[26px] bg-white shadow-sm ring-1 ring-slate-200">
                <Wallet className="h-10 w-10 text-slate-300" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">
                {authIssue
                  ? language === 'en'
                    ? 'Please Log In Again'
                    : 'Bitte erneut einloggen'
                  : language === 'en'
                    ? 'No Wallet Sets Yet'
                    : 'Noch keine Wallet Sets'}
              </h3>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                {authIssue
                  ? language === 'en'
                    ? 'For your wallet sets, a valid session is required. Please log in again to view your purchases.'
                    : 'Für Ihre Wallet Sets ist eine gültige Sitzung erforderlich. Bitte loggen Sie sich erneut ein, um Ihre Käufe anzuzeigen.'
                  : language === 'en'
                    ? 'Start your tax analysis journey by analyzing your first VeChain wallet set. Purchase a plan to unlock detailed tax calculations.'
                    : 'Starten Sie Ihre Steueranalyse-Reise, indem Sie Ihr erstes VeChain-Wallet Set analysieren. Kaufen Sie einen Plan, um detaillierte Steuerberechnungen freizuschalten.'}
              </p>
              <button
                type="button"
                onClick={
                  authIssue
                    ? () => {
                        localStorage.removeItem('v3tax_auth');
                        window.dispatchEvent(new Event('open-login'));
                      }
                    : onClose
                }
                className="mt-8 rounded-2xl bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700"
              >
                {authIssue
                  ? language === 'en'
                    ? 'Log In'
                    : 'Anmelden'
                  : language === 'en'
                    ? 'Start Analysis'
                    : 'Analyse starten'}
              </button>
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col gap-3">
            <div className="flex h-[52px] shrink-0 items-center justify-between gap-3 rounded-[24px] border border-slate-200/90 bg-white/95 px-3 py-2 shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
            <div className="min-w-0">
            <MyWalletYearTabs
            years={sortedYears}
            selectedYear={activeYear}
            onChange={(year) => {
            setSelectedYear(year);
            setSelectedSetIndexByYear((prev) => ({ ...prev, [year]: prev[year] ?? 0 }));
            }}
            />
            </div>
            
            <MyWalletSetSwitcher
            activeDataset={activeDataset}
            activeIndex={activeSetIndex}
            totalSets={setsForActiveYear.length}
            onPrevious={goToPreviousSet}
            onNext={goToNextSet}
            language={language}
            />
            </div>
            
            {!activeDataset ? (
            <div className="flex min-h-0 flex-1 items-center justify-center rounded-[28px] border border-dashed border-slate-200 bg-white/80 p-10 text-center shadow-sm">
            <div>
            <Wallet className="mx-auto mb-4 h-12 w-12 text-slate-300" />
            <h3 className="text-xl font-black text-slate-700">
            {language === 'en' ? 'No Wallet Set for this year' : 'Kein Wallet-Set für dieses Jahr'}
            </h3>
            <p className="mt-2 text-sm font-medium text-slate-500">
            {language === 'en' ? 'Choose another tax year above.' : 'Wähle oben ein anderes Steuerjahr.'}
            </p>
            </div>
            </div>
            ) : (
            (() => {
            const dataset = activeDataset;
            const txLimitExceeded = isDatasetOverPlanLimit(dataset);
            const hasSnapshot = dataset.snapshot_exists ?? dataset.snapshotExists ?? false;
            const effectivePlan = dataset.plan_effective || dataset.plan || 'Free';
            const nextPlans = getNextPlans(effectivePlan);
            const showUpgrade = txLimitExceeded && !hasSnapshot && nextPlans.length > 0;
            const isUpgradeOpen = openUpgradeFor === dataset.id;
            const chosenPlan = selectedPlanById[dataset.id] || nextPlans[0] || 'Pro';
            const chosenToken = (selectedTokenById[dataset.id] || 'vet').toLowerCase();
            const usage = getDatasetUsageMeta(dataset, language);
            const countryMeta = getCountryMeta(dataset.residence_country || 'DE');
            const lastCalculated =
            dataset.last_calculated_at ||
            dataset.lastCalculatedAt ||
            localStorage.getItem(`v3tax:lastCalculatedAt:${String(dataset.id)}`);
            const inactiveWallets = activeWallets.filter((w) => !w.is_active);
            const pageStart = activeWallets.length ? safeWalletPage * walletsPerPage + 1 : 0;
            const pageEnd = Math.min(activeWallets.length, safeWalletPage * walletsPerPage + visibleWallets.length);
            const planDisplayLabel = formatPlanLabel(effectivePlan);
            const setTitle = dataset.label || (language === 'en' ? `Wallet Set #${dataset.id}` : `Wallet Set #${dataset.id}`);
            
            return (
            <div className="grid min-h-0 flex-1 grid-rows-[auto_62px_minmax(0,1fr)] gap-4">
            <div className="overflow-hidden rounded-[28px] border border-slate-200/90 bg-white/95 p-4 shadow-[0_16px_38px_rgba(15,23,42,0.055)]">
            <div className="grid min-h-[168px] grid-cols-[minmax(360px,1.25fr)_minmax(190px,0.7fr)_minmax(320px,1fr)_minmax(180px,0.68fr)_minmax(180px,0.68fr)_minmax(155px,0.55fr)] items-stretch gap-4">
            <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50/75 p-4">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent" />
            
            <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[18px] bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-[0_12px_26px_rgba(37,99,235,0.22)]">
            <CreditCard className="h-5 w-5" />
            </div>
            
            <div className="min-w-0 flex-1">
            <h3 className="text-xl font-semibold leading-tight text-slate-950">
            {setTitle}
            </h3>

            <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0.5 text-sm font-semibold text-blue-700">
            <PlanBadge planId={effectivePlan} size="xxs" variant="compact" />
            {planDisplayLabel}
            </span>
            </div>
            
            <div className="mt-3 grid gap-1.5 text-sm font-normal text-slate-500">
            <div className="flex items-center gap-2">
            <img src={countryMeta.flagUrl} alt="" className="h-3.5 w-5 rounded-sm" />
            <span>{language === 'en' ? 'Tax rules' : 'Steuerlogik'}: <span className="font-medium text-slate-700">{countryMeta.name}</span></span>
            </div>
            
            <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-500" />
            <span className="truncate">
            {language === 'en' ? 'Created' : 'Erstellt'}: {formatCompactDateTime(dataset.purchaseDate)}
            </span>
            </div>
            
            <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-indigo-500" />
            <span className="truncate">
            {language === 'en' ? 'Calculated' : 'Berechnet'}: {lastCalculated ? formatCompactDateTime(lastCalculated) : '-'}
            </span>
            </div>
            </div>
            </div>
            </div>
            </div>
            
            <MyWalletMetricTile
            icon={Wallet}
            label={language === 'en' ? 'Wallets' : 'Wallets'}
            value={dataset.walletCount ?? activeWallets.length}
            subValue={language === 'en' ? 'in this set' : 'in diesem Set'}
            tone="blue"
            />
            
            <div className="flex h-full min-h-0 flex-col rounded-[22px] border border-slate-200/80 bg-white px-4 py-3 shadow-sm">
            <div className="flex min-h-0 flex-1 items-center gap-3">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[14px] ring-1 ${
            txLimitExceeded
            ? 'bg-rose-50 text-rose-700 ring-rose-100'
            : 'bg-indigo-50 text-indigo-700 ring-indigo-100'
            }`}>
            <TrendingUp className="h-[18px] w-[18px]" />
            </div>
            
            <div className="min-w-0">
            <div className="truncate text-xs font-semibold uppercase tracking-[0.10em] text-slate-400">
            {language === 'en' ? 'Usage' : 'Nutzung'}
            </div>
            <div className="mt-0.5 truncate text-base font-semibold leading-tight text-slate-900">
            {usage.isUnlimited
              ? `${usage.used.toLocaleString()} — ${usage.limitLabel}`
              : `${usage.used.toLocaleString()} / ${usage.planLimit.toLocaleString()}`}
            </div>
            <div className={`text-sm font-normal leading-snug ${txLimitExceeded ? 'text-rose-600' : 'text-slate-500'}`}>
            {usage.isUnlimited
              ? (language === 'en' ? 'Unlimited plan' : 'Unbegrenzter Plan')
              : `${usage.pct}% ${language === 'en' ? 'used' : 'genutzt'}`}
            </div>
            </div>
            </div>
            
            {!usage.isUnlimited && (
            <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div
            className={`h-full rounded-full ${
            txLimitExceeded
            ? 'bg-gradient-to-r from-rose-500 to-orange-500'
            : 'bg-gradient-to-r from-blue-500 to-indigo-500'
            }`}
            style={{ width: `${Math.min(usage.pct, 100)}%` }}
            />
            </div>
            )}
            </div>
            
            <MyWalletMetricTile
            icon={Layers}
            label={language === 'en' ? 'Rules' : 'Regeln'}
            value={countryMeta.code}
            subValue={countryMeta.name}
            tone="violet"
            />
            
            <MyWalletStatusTile
            hasSnapshot={hasSnapshot}
            manualOverrideCount={manualOverrideCount}
            language={language}
            isResetting={isResettingManualOverrides}
            onResetManualOverrides={() => handleResetManualOverridesOnly(dataset)}
            />
            
            <div className="flex h-full flex-col gap-2.5">
            <button
            type="button"
            onClick={(e) => {
            e.stopPropagation();
            openInvoicesForDataset(dataset);
            }}
            className="flex h-full flex-1 flex-col items-center justify-center gap-2 rounded-[20px] border border-blue-100 bg-blue-50/80 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100"
            >
            <FileText className="h-4 w-4" />
            {language === 'en' ? 'Invoice' : 'Rechnung'}
            </button>
            
            <div className="flex h-full flex-1 flex-col items-center justify-center rounded-[20px] border border-slate-200 bg-white shadow-sm">
            <div className="text-xl font-semibold text-blue-700">
            {dataset.walletCount ?? activeWallets.length}
            </div>
            <div className="text-xs font-medium uppercase tracking-[0.10em] text-slate-400">
            {language === 'en' ? 'Total' : 'Gesamt'}
            </div>
            </div>
            </div>
            </div>
            </div>
            
            <div className="relative rounded-[24px] border border-slate-200/90 bg-white/95 px-4 py-2 shadow-[0_12px_28px_rgba(15,23,42,0.045)]">
            <div className="flex h-full items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
            <CheckCircle className="h-4 w-4" />
            </div>
            
            <div className="min-w-0">
            <div className="text-base font-semibold text-slate-950">
            {language === 'en' ? 'Set Actions' : 'Set-Aktionen'}
            </div>
            <div className="truncate text-xs font-normal text-slate-500">
            {language === 'en' ? 'Manage the active wallet set' : 'Aktives Wallet-Set verwalten'}
            </div>
            </div>
            </div>
            
            <div className="flex shrink-0 items-center gap-2">
            <button
            onClick={() => {
            onClose();
            navigate('/', {
            state: {
            upgradeMode: true,
            datasetId: dataset.id,
            datasetLabel: dataset.label,
            currentWalletCount: dataset.walletCount || 0,
            currentTransactionCount: dataset.total_transactions || 0,
            planLimit: resolvePlanTxLimit(
              dataset.plan_effective || dataset.plan || 'Free',
              dataset.plan_limit
            ),
            plan: dataset.plan_effective || dataset.plan || 'Free',
            year: dataset.year,
            existingWallets: dataset.wallets || [],
            },
            });
            }}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
            >
            <Plus className="h-4 w-4" />
            {language === 'en' ? 'Add Wallet' : 'Wallet hinzufügen'}
            </button>
            
            <button
            onClick={() => setShowImportsFor(dataset.id)}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
            >
            <FileText className="h-4 w-4" />
            CSV Import
            </button>
            
            <button
            onClick={() => handleSyncDataset(dataset)}
            disabled={dataset.syncLimitReached}
            title={
              language === 'en'
                ? 'Refreshes on-chain data and invalidates the snapshot for a new calculation.'
                : 'Lädt On-Chain-Daten neu und invalidiert den Snapshot für eine Neuberechnung.'
            }
            className={`inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-45 ${
            dataset.syncLimitReached
            ? 'border border-slate-200 bg-slate-100 text-slate-400'
            : 'border border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700'
            }`}
            >
            <RefreshCw className="h-4 w-4" />
            Sync
            </button>
            
            {showUpgrade && (
            <button
            onClick={() => setOpenUpgradeFor(isUpgradeOpen ? null : dataset.id)}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:shadow-[0_10px_22px_rgba(16,185,129,0.28)]"
            >
            <ArrowUpCircle className="h-4 w-4" />
            Upgrade
            </button>
            )}
            
            {hasSnapshot ? (
            <button
            onClick={() => handleViewDatasetAnalysis(dataset)}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 text-sm font-semibold text-white shadow-[0_10px_22px_rgba(37,99,235,0.20)] transition hover:shadow-[0_14px_28px_rgba(37,99,235,0.24)]"
            >
            <BarChart3 className="h-4 w-4" />
            {language === 'en' ? 'View Analysis' : 'Analyse anzeigen'}
            </button>
            ) : (
            <button
            onClick={() => {
            if (txLimitExceeded) return;
            handleCalculateDataset(dataset);
            }}
            disabled={txLimitExceeded}
            className={`inline-flex h-10 items-center gap-2 rounded-xl px-5 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
            txLimitExceeded
            ? 'bg-slate-200 text-slate-500'
            : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
            }`}
            >
            <Zap className="h-4 w-4" />
            {txLimitExceeded
            ? (language === 'en' ? 'Limit exceeded' : 'Limit überschritten')
            : (language === 'en' ? 'Calculate Set' : 'Set berechnen')}
            </button>
            )}
            </div>
            </div>
            
            {showUpgrade && isUpgradeOpen && (
            <div className="absolute right-3 top-[70px] z-30 flex w-[580px] items-center gap-2 rounded-[22px] border border-blue-100 bg-white p-2 shadow-[0_24px_70px_rgba(15,23,42,0.18)]">
            {(() => {
            const a = getPlanAccent(chosenPlan);
            return (
            <div
            className={[
            'group relative flex flex-1 items-center gap-2 overflow-hidden rounded-2xl border border-slate-200 bg-white px-2 transition hover:shadow-md',
            a.border,
            a.ring,
            'ring-offset-2 ring-offset-white',
            ].join(' ')}
            >
            <PlanBadge planId={chosenPlan} size="xs" variant="compact" className="relative z-10 shrink-0" />
            
            <select
            value={chosenPlan}
            onChange={(e) => setSelectedPlanById((prev) => ({ ...prev, [dataset.id]: e.target.value }))}
            className="relative z-10 h-10 flex-1 bg-transparent text-sm font-semibold outline-none"
            >
            {nextPlans.map((p) => (
            <option key={p} value={p}>{p}</option>
            ))}
            </select>
            </div>
            );
            })()}
            
            <div className="flex flex-1 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-2">
            <img
            src={chosenToken === 'vet' ? '/vet-icon.png' : chosenToken === 'vtho' ? '/vtho-icon.png' : '/b3tr-icon.png'}
            alt={chosenToken?.toUpperCase()}
            className="h-4 w-4 object-contain"
            />
            
            <select
            value={chosenToken}
            onChange={(e) => setSelectedTokenById((prev) => ({ ...prev, [dataset.id]: e.target.value }))}
            className="h-10 flex-1 bg-transparent text-sm font-semibold outline-none"
            >
            <option value="vet">VET</option>
            <option value="vtho">VTHO</option>
            <option value="b3tr">B3TR</option>
            </select>
            </div>
            
            <button
            onClick={() => {
            window.dispatchEvent(
            new CustomEvent('upgrade:open', {
            detail: {
            datasetId: dataset.id,
            basePurchaseId: dataset.id,
            year: dataset.year,
            fromPlan: effectivePlan,
            toPlan: chosenPlan,
            token: chosenToken,
            residenceCountry:
              dataset.residence_country ||
              dataset.residenceCountry ||
              dataset.selectedCountry ||
              dataset.country ||
              null,
            },
            })
            );
            }}
            className="h-10 whitespace-nowrap rounded-2xl bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
            {language === 'en' ? 'Start' : 'Starten'}
            </button>
            </div>
            )}
            </div>
            
            <div className="flex min-h-0 flex-col overflow-hidden rounded-[30px] border border-slate-200/90 bg-white/95 p-5 shadow-[0_18px_44px_rgba(15,23,42,0.06)]">
            <div className="flex h-12 shrink-0 items-center justify-between gap-3">
            <div className="min-w-0">
            <div className="flex items-center gap-2">
            <h4 className="text-lg font-semibold text-slate-950">
            {language === 'en' ? 'Wallet Pairs' : 'Wallet-Pairs'}
            </h4>
            
            {inactiveWallets.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-700">
            <AlertCircle className="h-3.5 w-3.5" />
            {inactiveWallets.length} {language === 'en' ? 'inactive' : 'inaktiv'}
            </span>
            )}
            </div>
            
            <p className="text-sm font-normal text-slate-500">
            {activeWallets.length > 0
            ? (language === 'en'
            ? `Showing ${pageStart}-${pageEnd} of ${activeWallets.length}`
            : `Zeige ${pageStart}-${pageEnd} von ${activeWallets.length}`)
            : (language === 'en' ? 'No wallets in this set' : 'Keine Wallets in diesem Set')}
            </p>
            </div>
            
            <div className="flex items-center gap-2">
            <button
            type="button"
            onClick={goToPreviousWalletPage}
            disabled={safeWalletPage <= 0}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-50 text-slate-500 ring-1 ring-slate-200 transition hover:bg-white hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-35"
            >
            <ChevronLeft className="h-4 w-4" />
            </button>
            
            <span className="min-w-[62px] text-center text-base font-semibold tabular-nums text-slate-800">
            {safeWalletPage + 1} / {walletPageCount}
            </span>
            
            <button
            type="button"
            onClick={goToNextWalletPage}
            disabled={safeWalletPage >= walletPageCount - 1}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-50 text-slate-500 ring-1 ring-slate-200 transition hover:bg-white hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-35"
            >
            <ChevronRight className="h-4 w-4" />
            </button>
            </div>
            </div>
            
            {visibleWallets.length === 0 ? (
            <div className="mt-4 flex min-h-0 flex-1 items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50/70 text-center text-sm font-semibold text-slate-500">
            {language === 'en' ? 'No wallet pairs available for this set.' : 'Für dieses Set sind keine Wallet-Pairs verfügbar.'}
            </div>
            ) : (
            <div className={`mt-5 grid min-h-0 flex-1 auto-rows-min items-start gap-5 overflow-hidden ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
            {visibleWallets.map((wallet, index) => {
            const absoluteIndex = safeWalletPage * walletsPerPage + index;
            const totalTx = Number(wallet.total_tx_count ?? wallet.totalTxCount ?? 0);
            
            return (
            <div
            key={`${wallet.main_wallet}-${wallet.delegate_wallet}-${dataset.id}-${absoluteIndex}`}
            className={`flex h-[360px] min-h-0 flex-col rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm transition hover:border-blue-100 hover:shadow-md ${
            !wallet.is_active ? 'opacity-60' : ''
            }`}
            >
            <div className="mb-4 flex h-8 shrink-0 items-center justify-between gap-2">
            <button
            type="button"
            onClick={() => handleToggleWalletStatus(dataset.id, wallet)}
            disabled={togglingWalletId === wallet.wallet_pair_id}
            className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
            wallet.is_active
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            : 'border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100'
            }`}
            >
            <span className={`h-2 w-2 rounded-full ${wallet.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {wallet.is_active ? (language === 'en' ? 'Active' : 'Aktiv') : (language === 'en' ? 'Inactive' : 'Inaktiv')}
            </button>
            
            <span className="rounded-full bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-400 ring-1 ring-slate-100">
            #{absoluteIndex + 1}
            </span>
            </div>
            
            <div className="min-h-0 flex-1 space-y-3">
            <MyWalletAddressBox
            label={language === 'en' ? 'Main Wallet' : 'Main-Wallet'}
            address={wallet.main_wallet || ''}
            tone="blue"
            onCopy={copyToClipboard}
            language={language}
            />

            <MyWalletAddressBox
            label={language === 'en' ? 'VeDelegate Wallet' : 'VeDelegate-Wallet'}
            address={wallet.delegate_wallet || ''}
            tone="emerald"
            onCopy={copyToClipboard}
            language={language}
            />
            </div>
            
            <div className="mt-5 flex h-[60px] shrink-0 items-center justify-between rounded-[21px] border border-slate-200 bg-slate-50/80 px-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-blue-700">
            <TrendingUp className="h-4 w-4" />
            {language === 'en' ? 'Transactions' : 'Transaktionen'}
            </div>
            
            <div className="text-2xl font-semibold text-blue-800">
            {totalTx.toLocaleString()}
            </div>
            </div>
            </div>
            );
            })}
            </div>
            )}
            </div>
            </div>
            );
            })()
            )}
            </div>
          )}
          </div>
        </div>
      </div>
    </ModalMotionShell>
      
      {/* Dataset Invoices Modal */}
      <DatasetInvoicesModal
        isOpen={showInvoices}
        onClose={() => {
          setShowInvoices(false);
          setInvoiceContext(null);
        }}
        year={invoiceContext?.year}
        currentPlan={invoiceContext?.plan}
        basePurchaseId={invoiceContext?.purchaseId}
      />
      
      {/* Exchange Imports Modal */}
      <ExchangeImportsManager
        isOpen={!!showImportsFor}
        datasetId={showImportsFor}
        onClose={() => {
          setShowImportsFor(null);
          pendingImportsRef.current = null; // Reset wenn manuell geschlossen
        }}
        onOpenSupport={() => {
          const currentDataset = showImportsFor;
          pendingImportsRef.current = currentDataset;

          setShowImportsFor(null);

          if (onOpenSupport) {
            onOpenSupport(() => {
              if (pendingImportsRef.current) {
                setShowImportsFor(pendingImportsRef.current);
                pendingImportsRef.current = null;
              }
            });
          }
        }}
      />
    </>
  );
};

export default MyWalletsDashboard;
